import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import de from "@/locales/de.json";

// Nicht-DE-Sprachen werden per dynamic import erst geladen, wenn sie gewaehlt
// werden. Das halt das initiale Bundle klein, auch wenn spaeter 30+ Sprachen
// dazukommen. DE bleibt als Fallback eager im Bundle.
const LAZY_BUNDLES: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import("@/locales/en.json"),
  es: () => import("@/locales/es.json"),
};

async function loadLanguage(lng: string): Promise<void> {
  const base = lng.slice(0, 2);
  if (base === "de") return;
  if (i18n.hasResourceBundle(base, "translation")) return;
  const loader = LAZY_BUNDLES[base];
  if (!loader) return;
  const mod = await loader();
  i18n.addResourceBundle(base, "translation", mod.default, true, true);
}

/** Sprache wechseln — laedt das Bundle ZUERST und triggert dann den
 * React-Rerender via ``changeLanguage``. Wenn wir die Reihenfolge
 * umdrehen (wie es ``languageChanged``-Hook tat), feuert der Rerender
 * bevor das Bundle da ist und die UI bleibt auf der alten Sprache. */
export async function selectLanguage(lng: string): Promise<void> {
  await loadLanguage(lng);
  await i18n.changeLanguage(lng);
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      de: { translation: de },
    },
    fallbackLng: "de",
    supportedLngs: ["de", "en", "es"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "bm_lang",
      caches: ["localStorage"],
    },
  })
  .then(() => loadLanguage(i18n.language))
  .catch(() => undefined);

// Safety-Net: falls irgendwo im Code ``changeLanguage`` direkt aufgerufen
// wird (ohne selectLanguage zu nehmen), sorgen wir zumindest dafuer, dass
// das Bundle nachgeladen wird — der Rerender ist dann aber erst beim
// naechsten Render-Zyklus korrekt (z. B. nach Navigation).
i18n.on("languageChanged", (lng) => {
  void loadLanguage(lng);
});

export default i18n;
